import { ReactElement } from "react";
import {
	DetailsBox,
	DateString,
	DropOver,
	FormattedText,
	Space,
	WeekViewCardStyle
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { toStringAlpabeticallySorted } from "./misc";
import { AppointmentKind, ZermeloAppointment } from "client/Api/Workspace/LiveInformation/Zermelo/zermeloLiveInformation";
import { DestinationWindow } from "client/Api/Apps/common";
import { TimeZone } from "client/DateAndTime/timezones";

const {
	Divider,
	Item,
	Anchor
} = DetailsBox;

export type ZermeloAppointmentDetailsProps = {
	appointment: ZermeloAppointment;
	destinationWindow: DestinationWindow;
	onClose(): void;
	variant: WeekViewCardStyle | undefined;
	target: HTMLElement;
	timezone: TimeZone;
};

export const ZermeloAppointmentDetails = (props: ZermeloAppointmentDetailsProps): ReactElement => {
	const {
		appointment,
		destinationWindow,
		onClose,
		variant,
		target,
		timezone
	} = props;

	const {
		subjects,
		startDateTime,
		endDateTime,
		teachers,
		locations,
		groups,
		remarks,
		url,
		isOnline,
		kind,
		choice
	} = appointment;

	return (
		<DropOver
			target={target}
			onClose={onClose}
		>
			<DetailsBox
				variant={variant}
				title={toStringAlpabeticallySorted(subjects).toUpperCase()}
				onClose={onClose}
			>
				<DetailsBox.List>
					{
						kind === AppointmentKind.Activity && (
							<Item
								icon={{
									kind: "font",
									name: "check",
									color: "success"
								}}
							>
								{localize("_Activity")}
							</Item>
					)}
					{
						kind === AppointmentKind.Exam && (
							<Item
								icon={{
									kind: "font",
									name: "file-lines",
									color: "warning"
								}}
							>
								{localize("_Exam")}
							</Item>
					)}
					{
						kind === AppointmentKind.Conflict && (
							<Item
								icon={{
									kind: "font",
									name: "triangle-exclamation",
									color: "warning"
								}}
							>
								{localize("_Conflict")}
							</Item>
					)}
					<Divider />
					<Item icon="clock">
						<DateString
							value={startDateTime}
							format="DMY"
							timezone={timezone}
						/>
						<Space />
						<DateString
							value={startDateTime}
							format="T"
							timezone={timezone}
						/>
						<Space />-<Space />
						<DateString
							value={endDateTime}
							format="T"
							timezone={timezone}
						/>
					</Item>
					{
						locations.length > 0 && (
							<Item icon="location-dot">
								{locations.join()}
							</Item>
					)}
					{
						teachers.length > 0 && (
							<Item icon="user-tie">
								{teachers.join().toUpperCase()}
							</Item>
					)}
					{
						groups.length > 0 && (
							<Item icon="user-group">
								{groups.join()}
							</Item>
					)}
					{ choice.isSet && choice.value.hasSelected && (
						<>
							<Divider />
							{ choice.value.canBeChanged
								? (
									<Item
										icon={{
											kind: "font",
											name: "check",
											color: "success"
										}}
									>
										{localize("_ChoiceMadeCanChange")}
									</Item>
								)
								: (
									<Item icon="check">
										{localize("_ChoiceMadeCantChange")}
									</Item>
								)

							}
						</>
					)}
					{
						isOnline && (
							<Item icon="video">
								{localize("_OnlineLesson")}
							</Item>
					)}
					{
						remarks.length > 0 && (
							<>
								<Divider />
								<Item icon="circle-info">
									{remarks.join()}
								</Item>
							</>
					)}
					<Anchor
						href={url}
						target={destinationWindow === DestinationWindow.NewWindow ? "_blank" : undefined}
					>
						<FormattedText
							text={localize("_OpenInAppName")}
							thingsToReplace={[
								{ what: "{0}", filler: localize("_Zermelo") }
							]}
						/>
					</Anchor>
				</DetailsBox.List>
			</DetailsBox>
		</DropOver>
	);
};
ZermeloAppointmentDetails.displayName = "ZermeloAppointmentDetails";